import React, { useEffect, useMemo, useState } from "react";
import {
  StudentNavbar,
  SubjectCard,
  ReleasedSubjectsCard,
} from "../components";
import axios from "axios";
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";
import { API_BASE_URL } from "../config";
import { getSubjectMeta } from "../constants/subjectMeta";

const slugify = (s = "") =>
  String(s)
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");

const chunk = (items, size) => {
  const pages = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages;
};

const Subjects = () => {
  const userInfo = JSON.parse(localStorage.getItem("user") || "{}");

  const [hovered, setHovered] = useState(null);
  const [progressBySubjectId, setProgressBySubjectId] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  const [releasedPage, setReleasedPage] = useState(0);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setSubjectsLoading(true);

        const response = await axios.get(`${API_BASE_URL}/subjects`);
        const rawSubjects = response.data || [];

        const normalizedSubjects = rawSubjects
          .map((subject) => {
            const examSlug = String(subject.exam_type || "").toLowerCase();
            const subjectSlug = slugify(subject.subject_name);
            const meta = getSubjectMeta(examSlug, subjectSlug);

            return {
              subjectId: subject.id,
              slug: subjectSlug,
              examSlug,
              title: meta?.label || subject.subject_name,
              exam: meta?.examLabel || String(subject.exam_type || "").toUpperCase(),
              image: meta?.image || null,
              isReleased: Boolean(subject.is_released),
            };
          })
          .filter((subject) => subject.title && subject.exam && subject.image)
          .sort((a, b) => {
            if (a.examSlug !== b.examSlug) {
              return a.examSlug.localeCompare(b.examSlug);
            }
            return a.title.localeCompare(b.title);
          });

        setSubjects(normalizedSubjects);
      } catch (error) {
        console.error("Error fetching subjects:", error);
        setSubjects([]);
      } finally {
        setSubjectsLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!userInfo?.id) {
        setProgressBySubjectId({});
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/enrollments`, {
          params: { student_id: userInfo.id },
        });

        const enrollments = response.data || [];
        const next = enrollments.reduce((acc, enrollment) => {
          if (enrollment.subject_id != null) {
            acc[enrollment.subject_id] = enrollment.progress || 0;
          }
          return acc;
        }, {});

        setProgressBySubjectId(next);
      } catch (error) {
        console.error("Error fetching subject progress:", error);
        setProgressBySubjectId({});
      }
    };

    fetchProgress();
  }, [userInfo?.id]);

  const released = useMemo(
    () => subjects.filter((subject) => subject.isReleased),
    [subjects]
  );

  const comingSoon = useMemo(
    () =>
      subjects
        .filter((subject) => !subject.isReleased)
        .sort((a, b) => {
          if (a.title === "Mathematics") return -1;
          if (b.title === "Mathematics") return 1;
          return 0;
        }),
    [subjects]
  );

  const releasedPages = useMemo(() => chunk(released, 4), [released]);

  useEffect(() => {
    if (releasedPages.length === 0) {
      setReleasedPage(0);
      return;
    }

    if (releasedPage > releasedPages.length - 1) {
      setReleasedPage(0);
    }
  }, [releasedPage, releasedPages.length]);

  const visibleReleased = releasedPages[releasedPage] || [];

  const releasedGridClass =
    visibleReleased.length <= 1
      ? "grid-cols-1"
      : visibleReleased.length === 2
      ? "grid-cols-1 md:grid-cols-2"
      : visibleReleased.length === 3
      ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
      : "grid-cols-1 md:grid-cols-2 xl:grid-cols-4";

  const usingPager = released.length > 4;

  const goPrev = () => {
    setReleasedPage((prev) =>
      prev === 0 ? releasedPages.length - 1 : prev - 1
    );
  };

  const goNext = () => {
    setReleasedPage((prev) =>
      prev === releasedPages.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div className="min-h-screen w-full">
      <StudentNavbar />

      <div className="w-full px-4 sm:px-8 lg:px-12 xl:px-16 py-6">
        <div className="max-w-screen-xl 2xl:max-w-screen-2xl mx-auto">
          <div className="mb-6 flex items-center justify-between gap-4">
            <p className="font-extralight text-white text-2xl sm:text-3xl lg:text-4xl">
              Released Subjects
            </p>

            {usingPager && (
              <div className="hidden sm:flex items-center gap-2">
                <button
                  type="button"
                  onClick={goPrev}
                  className="rounded-full border border-white/15 bg-white/10 p-2 text-white hover:bg-white/20 transition"
                  aria-label="Previous released subjects"
                >
                  <RiArrowLeftSLine size={22} />
                </button>

                <button
                  type="button"
                  onClick={goNext}
                  className="rounded-full border border-white/15 bg-white/10 p-2 text-white hover:bg-white/20 transition"
                  aria-label="Next released subjects"
                >
                  <RiArrowRightSLine size={22} />
                </button>
              </div>
            )}
          </div>

          <div className="mb-10">
            {subjectsLoading ? (
              <div className="w-full rounded-2xl border border-white/10 bg-[#022935] p-8 text-center text-white/70">
                Loading subjects...
              </div>
            ) : released.length === 0 ? (
              <div className="w-full rounded-2xl border border-white/10 bg-[#022935] p-8 text-center text-white/70">
                No released subjects yet.
              </div>
            ) : released.length === 1 ? (
              <ReleasedSubjectsCard
                variant="featured"
                subject_title={released[0].title}
                exam_type={released[0].exam}
                subject_image={released[0].image}
                subjectId={released[0].subjectId}
              />
            ) : (
              <>
                <div className={`grid ${releasedGridClass} gap-4 lg:gap-5`}>
                  {visibleReleased.map((subject) => (
                    <ReleasedSubjectsCard
                      key={`${subject.examSlug}-${subject.slug}`}
                      variant="compact"
                      subject_title={subject.title}
                      exam_type={subject.exam}
                      subject_image={subject.image}
                      subjectId={subject.subjectId}
                    />
                  ))}
                </div>

                {usingPager && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {releasedPages.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setReleasedPage(index)}
                        aria-label={`Go to released subject page ${index + 1}`}
                        className={`h-2.5 rounded-full transition-all duration-200 ${
                          index === releasedPage
                            ? "w-8 bg-white"
                            : "w-2.5 bg-white/35 hover:bg-white/55"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="mb-6">
            <p className="font-extralight text-white text-2xl sm:text-3xl lg:text-4xl">
              Coming Soon
            </p>
          </div>

          {comingSoon.length === 0 ? (
            <div className="w-full rounded-2xl border border-white/10 bg-[#022935] p-6 text-center text-white/70">
              No upcoming subjects right now.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
              {comingSoon.map((subject) => {
                const hoverKey = `${subject.examSlug}-${subject.slug}`;

                return (
                  <SubjectCard
                    key={hoverKey}
                    subject_title={subject.title}
                    exam_type={subject.exam}
                    subject_image={subject.image}
                    subject_progress={progressBySubjectId[subject.subjectId] || 0}
                    isHovered={hovered === hoverKey}
                    setIsHovered={(value) => setHovered(value ? hoverKey : null)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subjects;